/**
 * The oral exam as something a candidate sits, and a model marks.
 *
 * A session is one sheet worked through out loud: the examiner's lines come
 * from a voice, every answer is its own recording, each recording is
 * transcribed as it lands, and the whole performance is marked once at the end.
 * Nothing about an oral exam is specific to telc except the parts it has and
 * the criteria it is marked on, so those are data (`OralFormat`) and the rest
 * — the recorder, the clips, the transcripts, the partner in a discussion — is
 * shared. telc's format is the only one filled in; the state exam's goes here
 * too when its sheets exist.
 *
 * **The model judges; it never adds up.** It places each criterion in one of
 * telc's six bands, and `speakingPoints()` turns the bands into points — the
 * same rule as the writing marking.
 *
 * **What is heard and what is not.** The model reads a transcript, not the
 * recording. Fluency it can measure (words per minute, pauses — `clipStats`,
 * from the transcription's word timings); pronunciation it can only infer,
 * from words the transcription could not make out as intended. The result
 * screen says so under the marks, every time.
 */
import type { Level } from "./structure.ts";

/** telc's five sections, in the order they are sat. Ids match `SPEAKING_CRITERIA`. */
export type OralPartId = "cz1a" | "cz1b" | "cz2a" | "cz2b" | "cz3";

export type OralSpeaker = "examiner" | "partner" | "candidate";

/**
 * One turn of the conversation, as stored.
 *
 * `index` counts within the part: the second of two examiner questions in 1B is
 * `{ part: "cz1b", index: 1 }`, and the candidate's answer to it has the same
 * index. The discussion alternates candidate and partner, both counting up.
 */
export interface OralTurn {
  part: OralPartId;
  index: number;
  speaker: OralSpeaker;
  text: string;
  /** Candidate turns only: what the timing of the words says. */
  stats?: ClipStats;
}

/** Measured off one recording's word timings — never estimated by a model. */
export interface ClipStats {
  /** Length of the recording. */
  seconds: number;
  words: number;
  /** From the first word to the last: leading and trailing silence is not speaking. */
  speakingSeconds: number;
  /** Silences of `LONG_PAUSE` seconds or more between two words. */
  longPauses: number;
  longestPause: number;
  /** "yyy", "eee", "hmm" — as transcribed, or tagged as an audio event. */
  fillers: number;
  /**
   * Words the transcription was unsure of (`UNCLEAR_BELOW`), fillers left out.
   * The nearest thing to pronunciation a transcript has: Scribe writes a badly
   * pronounced word down correctly more often than not, from context, but it
   * says how sure it was. Absent on sessions from before it was measured.
   */
  unclear?: number;
  /**
   * Silences of `MID_PAUSE` seconds or more between two words — the pauses
   * `longPauses` misses by a few tenths (a candidate whose longest was 1.9 s
   * had "none"). Measured, not yet shown to the model. Absent before 2026-10-20.
   */
  pauses1s?: number;
  /**
   * All silence of `PAUSE_FLOOR` seconds or more between two words, summed.
   * With `speakingSeconds` it separates how fast someone speaks when they do
   * from how much of the time they are silent: `articulationRate`.
   */
  pauseSeconds?: number;
}

/**
 * A word recognised with less confidence than this is counted as unclear.
 * Measured on one Polish sentence: a native voice had none of 27 words under
 * it, two foreign-accented voices over half.
 */
export const UNCLEAR_BELOW = 0.9;

/** A pause this long mid-answer is a search for a word, not a breath. */
export const LONG_PAUSE = 2;

/** The shorter pause counted beside `LONG_PAUSE`, as `pauses1s`. */
export const MID_PAUSE = 1;

/** Below this a gap is the rhythm of speech, not a pause. */
export const PAUSE_FLOOR = 0.5;

/**
 * One step of a session, as the browser runs it.
 *
 * `targetSeconds` is what the part is meant to take and what the screen counts
 * towards; `maxSeconds` is where the recording stops by itself, because an
 * answer that runs past it would run past the examiner in the hall too.
 */
export interface OralStep {
  part: OralPartId;
  /**
   * `monologue` — one answer to what is on the sheet (a picture, a presentation).
   * `questions` — the examiner asks `count` questions; each gets its own answer.
   * `discussion` — turns with a partner until the clock runs out.
   */
  kind: "monologue" | "questions" | "discussion";
  count?: number;
  targetSeconds: number;
  maxSeconds: number;
}

export interface OralFormat {
  /** Time to prepare before the first question, shown with a "start now" button. */
  prepSeconds: number;
  steps: OralStep[];
  /** Candidate turns the discussion may take, whatever the clock says. */
  maxDiscussionTurns: number;
}

/**
 * telc B1·B2 Szkoła, from the mock paper (pp. 43-44): part 1 is a picture and
 * one or two questions, about two minutes a candidate; part 2 is a prepared
 * presentation and one or two questions about it; part 3 is four minutes of
 * discussion between the two candidates. Twenty minutes to prepare beforehand.
 *
 * The second candidate's own picture and presentation are left out: a partner
 * who is not there has nothing to show, and the candidate is not marked on
 * listening to them. The partner exists only where the exam needs one to talk
 * to — the discussion.
 */
export const TELC_ORAL: OralFormat = {
  prepSeconds: 20 * 60,
  steps: [
    { part: "cz1a", kind: "monologue", targetSeconds: 60, maxSeconds: 75 },
    { part: "cz1b", kind: "questions", count: 2, targetSeconds: 30, maxSeconds: 45 },
    { part: "cz2a", kind: "monologue", targetSeconds: 120, maxSeconds: 180 },
    { part: "cz2b", kind: "questions", count: 2, targetSeconds: 30, maxSeconds: 45 },
    { part: "cz3", kind: "discussion", targetSeconds: 240, maxSeconds: 60 },
  ],
  maxDiscussionTurns: 8,
};

/** The step that runs a part. */
export const stepOf = (format: OralFormat, part: OralPartId): OralStep | undefined =>
  format.steps.find((s) => s.part === part);

/**
 * How many answers a part may hold — what the Worker checks an upload against.
 * A discussion takes one per turn; everything else what its step asks for.
 */
export function answersIn(format: OralFormat, part: OralPartId): number {
  const step = stepOf(format, part);
  if (!step) return 0;
  if (step.kind === "discussion") return format.maxDiscussionTurns;
  return step.count ?? 1;
}

/** An upload larger than this is not a spoken answer. Three minutes of Opus is ~1.5 MB. */
export const ORAL_CLIP_MAX_BYTES = 8 * 1024 * 1024;

/* ------------------------------------------------------------------ */
/* How many sessions an account has                                    */
/* ------------------------------------------------------------------ */

/**
 * One marked session on the house, per account, for life. It is how somebody
 * finds out whether a transcript and a model are any use to them before paying.
 */
export const FREE_ORAL_SESSIONS = 1;

/**
 * Sessions a pass brings, per ninety days of it — the 90-day pass brings ten,
 * the 180-day one twenty, a grant of thirty days three.
 *
 * Counted off the pass's length rather than stored on it, so a grant, an
 * extension and a refund (which shortens the pass it takes back) all come out
 * right with nothing written anywhere. A session costs us tens of groszy; the
 * limit is there so that a pass cannot be turned into a bill, not to ration
 * practice — ten full sittings in ninety days is more than anybody preparing
 * for one exam does.
 */
export const ORAL_SESSIONS_PER_90_DAYS = 10;

const DAY_MS = 86_400_000;

/** Where a session's allowance came from. `grant` is given by hand on `/admin`. */
export type OralPool = "free" | "pass" | "grant" | "pack";

/**
 * Sessions left, pool by pool.
 *
 * Each session records the pool it was spent from, and the pools are counted
 * apart, because they end differently: the free one never comes back, a pass's
 * lapse with the pass, a pack's never lapse. Counted as one number, ten pass
 * sessions spent before the pass ran out would be taken out of a pack bought
 * afterwards.
 *
 * `passes` is every pass the account ever had; their sessions count only while
 * one of them runs. `packSessions` is every paid, unrefunded pack's sessions.
 * `grantSessions` is what the admin gave by hand, less what was taken back —
 * like a pack's, they never lapse, but nobody paid for them.
 */
export function oralBalance(input: {
  passes: { startsAt: number; expiresAt: number }[];
  packSessions: number;
  grantSessions?: number;
  used: Partial<Record<OralPool, number>>;
  now: number;
}): { free: number; pass: number; grant: number; pack: number; left: number } {
  const running = input.passes.some((p) => p.expiresAt > input.now);
  const passTotal = running
    ? input.passes.reduce((sum, p) => {
        const days = Math.max(0, (p.expiresAt - p.startsAt) / DAY_MS);
        return sum + Math.round((days * ORAL_SESSIONS_PER_90_DAYS) / 90);
      }, 0)
    : 0;
  const free = Math.max(0, FREE_ORAL_SESSIONS - (input.used.free ?? 0));
  const pass = Math.max(0, passTotal - (input.used.pass ?? 0));
  const grant = Math.max(0, (input.grantSessions ?? 0) - (input.used.grant ?? 0));
  const pack = Math.max(0, input.packSessions - (input.used.pack ?? 0));
  return { free, pass, grant, pack, left: free + pass + grant + pack };
}

/**
 * The pool a new session is spent from: the free one first, then the pass —
 * whose sessions lapse — then a grant, and the pack, which was paid for and
 * keeps, last.
 */
export function poolToSpend(b: { free: number; pass: number; grant?: number; pack: number }): OralPool | null {
  if (b.free > 0) return "free";
  if (b.pass > 0) return "pass";
  if ((b.grant ?? 0) > 0) return "grant";
  if (b.pack > 0) return "pack";
  return null;
}

/* ------------------------------------------------------------------ */
/* The marking                                                         */
/* ------------------------------------------------------------------ */

/** Where a criterion was placed: 5 = B2 upper … 0 = below A2, as on p. 38. */
export type OralBand = 0 | 1 | 2 | 3 | 4 | 5;

export interface OralCriterionVerdict {
  band: OralBand;
  points: number;
  max: number;
  comment: string;
}

export interface OralCorrection {
  /** Verbatim from the transcript. */
  quote: string;
  fix: string;
  why: string;
}

export interface OralReview {
  /** Keyed by `SPEAKING_CRITERIA` id: the five parts, then the four language criteria. */
  criteria: Record<string, OralCriterionVerdict>;
  points: number;
  max: number;
  level: Level;
  corrections: OralCorrection[];
  improvements: string[];
  summary: string;
  /** The whole performance, measured: every candidate clip added together. */
  stats: ClipStats;
  lang: string;
  model: string;
  createdAt: number;
}

/**
 * Timing stats of one recording, from word-level timestamps.
 *
 * `words` is what a transcription returns — spacing and audio events included,
 * as ElevenLabs' Scribe does — and nothing here trusts it to be sorted or
 * complete: an entry without a time is counted as a word and not as a moment.
 */
export function clipStats(
  words: { text: string; type: string; start: number | null; end: number | null; logprob?: number | null }[],
  seconds: number,
): ClipStats {
  const spoken = words.filter((w) => w.type === "word");
  const timed = spoken.filter(
    (w): w is typeof w & { start: number; end: number } =>
      typeof w.start === "number" && typeof w.end === "number",
  );
  let longPauses = 0;
  let longestPause = 0;
  let pauses1s = 0;
  let pauseSeconds = 0;
  for (let i = 1; i < timed.length; i++) {
    const gap = timed[i]!.start - timed[i - 1]!.end;
    if (gap >= LONG_PAUSE) longPauses++;
    if (gap >= MID_PAUSE) pauses1s++;
    if (gap >= PAUSE_FLOOR) pauseSeconds += gap;
    if (gap > longestPause) longestPause = gap;
  }
  const fillers =
    spoken.filter((w) => FILLER.test(w.text.trim())).length +
    words.filter((w) => w.type === "audio_event" && /hes|umm|uh|filler|wahani/i.test(w.text)).length;
  const first = timed[0];
  const last = timed[timed.length - 1];
  const rated = spoken.filter((w) => typeof w.logprob === "number" && !FILLER.test(w.text.trim()));
  return {
    seconds: round1(seconds),
    words: spoken.length - spoken.filter((w) => FILLER.test(w.text.trim())).length,
    speakingSeconds: first && last ? round1(last.end - first.start) : 0,
    longPauses,
    longestPause: round1(longestPause),
    fillers,
    pauses1s,
    pauseSeconds: round1(pauseSeconds),
    ...(rated.length ? { unclear: rated.filter((w) => Math.exp(w.logprob!) < UNCLEAR_BELOW).length } : {}),
  };
}

/**
 * A hesitation as Scribe writes one down. Not a lone "a" — that is the
 * conjunction ("a starsza pani…"), and a lone "e" or "y" is never a word.
 */
const FILLER = /^(y+|e+|e+m+|y+m+|h+m+|m{2,}|a{2,})[.,!?…]*$/iu;

const round1 = (n: number): number => Math.round(n * 10) / 10;

/** Several clips as one: what the whole session measured. */
export function sumStats(all: ClipStats[]): ClipStats {
  return all.reduce<ClipStats>(
    (sum, s) => ({
      seconds: round1(sum.seconds + s.seconds),
      words: sum.words + s.words,
      speakingSeconds: round1(sum.speakingSeconds + s.speakingSeconds),
      longPauses: sum.longPauses + s.longPauses,
      longestPause: Math.max(sum.longestPause, s.longestPause),
      fillers: sum.fillers + s.fillers,
      ...(sum.unclear !== undefined || s.unclear !== undefined
        ? { unclear: (sum.unclear ?? 0) + (s.unclear ?? 0) }
        : {}),
      ...(sum.pauses1s !== undefined || s.pauses1s !== undefined
        ? { pauses1s: (sum.pauses1s ?? 0) + (s.pauses1s ?? 0) }
        : {}),
      ...(sum.pauseSeconds !== undefined || s.pauseSeconds !== undefined
        ? { pauseSeconds: round1((sum.pauseSeconds ?? 0) + (s.pauseSeconds ?? 0)) }
        : {}),
    }),
    { seconds: 0, words: 0, speakingSeconds: 0, longPauses: 0, longestPause: 0, fillers: 0 },
  );
}

/** Share of the words the transcription was unsure of, in per cent; null where not measured. */
export const unclearPercent = (s: ClipStats): number | null =>
  s.unclear === undefined || s.words === 0 ? null : Math.round((s.unclear / s.words) * 100);

/**
 * The highest band pronunciation and intonation may get, whatever the model
 * read into the transcript.
 *
 * Intonation does not survive speech broken every few words by a pause or a
 * "yyy", so it stays within a band of fluency. And a transcription unsure of
 * many words heard something it could not place, however tidy the text it
 * wrote down. Without these the criterion defaulted to "the text reads fine",
 * which Scribe makes true of nearly anybody.
 */
export function pronunciationCeiling(fluencyBand: number, unclear: number | null): number {
  const byFluency = fluencyBand + 1;
  const byClarity =
    unclear === null ? 5 : unclear <= 5 ? 5 : unclear <= 10 ? 4 : unclear <= 20 ? 3 : unclear <= 30 ? 2 : unclear <= 45 ? 1 : 0;
  return Math.max(0, Math.min(5, byFluency, byClarity));
}

/**
 * Fewer words than this in the whole session, and no language criterion goes
 * above A2. Four criteria judged on what little there is read "Nie wiem" as
 * faultless grammar: 48 words, no presentation, and 25 of the 50 language
 * points carried the session over B1's line. A B1 performance across all five
 * parts runs to several hundred words; a hundred is a minute and a half.
 */
export const LANGUAGE_EVIDENCE_WORDS = 100;

/** The highest band pronunciation, fluency, grammar and vocabulary may get. */
export const languageCeiling = (words: number): number => (words < LANGUAGE_EVIDENCE_WORDS ? 1 : 5);

/**
 * Words a minute while actually speaking — the pauses of `PAUSE_FLOOR` and over
 * taken out. Null where they were not measured. A slow `wordsPerMinute` with a
 * fast articulation rate is a candidate who stops to think; slow in both is one
 * who searches for each word.
 */
export const articulationRate = (s: ClipStats): number | null => {
  if (s.pauseSeconds === undefined) return null;
  const talking = s.speakingSeconds - s.pauseSeconds;
  return talking > 0 ? Math.round((s.words / talking) * 60) : null;
};

/** Words a minute of actual speech — the figure the fluency comment talks about. */
export const wordsPerMinute = (s: ClipStats): number =>
  s.speakingSeconds > 0 ? Math.round((s.words / s.speakingSeconds) * 60) : 0;
